import type { GetAccountContextResp } from "@/types/accounts.ts";
import type { MarkerReporter, MarkerSdk } from "@marker.io/browser";
import { createContext, useContext, useEffect, useState } from "react";
import { initializeMarkerSingleton } from "./sdk.ts";

interface MarkerIoContextValue {
  markerSdk: MarkerSdk | null;
}

const MarkerIoContext = createContext<MarkerIoContextValue>({
  markerSdk: null,
});

/**
 * Provider for the Marker.io SDK context. Initializes the SDK and makes it available to child components.
 */
export const MarkerIoProvider = ({
  children,
  userContext,
}: {
  children: React.ReactNode;
  userContext: GetAccountContextResp;
}) => {
  const [markerSdk, setMarkerSdk] = useState<MarkerSdk | null>(null);

  useEffect(() => {
    const initializeMarker = async () => {
      try {
        // this is a singleton internally, it's okay to call this multiple times on userContext change
        const sdk = await initializeMarkerSingleton(userContext);

        if (userContext) {
          const reporter: MarkerReporter = {
            email:
              userContext.user.primaryContact.handles?.find(
                (h) => h.type === "emailAddress" && h.isDefault,
              )?.value || "unknown email",
            fullName: userContext.user.userContact.name || "unknown name",
          };

          // For admins: this logs a "(Marker.io) Skipping reporter info (logged-in)" for you if you have a Marker.io account.
          // It's harmless: it's just telling you this is a no-op because your account takes precedence.
          sdk.setReporter(reporter);
        }

        setMarkerSdk(sdk);
      } catch (error) {
        console.warn("Failed to initialize Marker.io:", error);
      }
    };

    initializeMarker();
  }, [userContext]);

  return <MarkerIoContext.Provider value={{ markerSdk }}>{children}</MarkerIoContext.Provider>;
};

/**
 * Hook to access the Marker.io SDK instance.
 */
export const useMarkerSdk = () => {
  const context = useContext(MarkerIoContext);
  if (!context) {
    throw new Error("useMarkerSdk must be used within a MarkerIoProvider");
  }
  return context;
};
